import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { NumberInput } from "react-admin";
import { DateTime } from "luxon";
import { z } from "zod";
import { runSafelyWithServerClient, schemas } from "../api/server.api";
import { SaveButton2 } from "../buttons/SaveButton2";
import { DateInput } from "../inputs/DateInput";
import { SelectInput } from "../inputs/SelectInput";
import { DrivingLicenseClassEnum, type DrivingLicenseClass } from "../model/DrivingLicenseClass";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import type { DialogProps } from "../misc/DialogProps";
import { Form2 } from "../misc/Form2";
import { useDialog } from "../hooks/useDialog";
import { ConfirmDialog } from "./ConfirmDialog";

type ExternalTrainingProgress = z.infer<typeof schemas.ExternalTrainingProgressDto>;
type ProgressBody = z.infer<typeof schemas.CreateExternalTrainingProgressDto>;
type FormValues = Omit<
  ProgressBody,
  | "testfahrt_schaltkompetenz_date"
  | "theory_exam_date"
  | "practical_exam_date"
  | "theory_exam_result"
  | "practical_exam_result"
> & {
  drivingLicenseClass: DrivingLicenseClass | "";
  testfahrt_schaltkompetenz_date: DateTime | null;
  theory_exam_date: DateTime | null;
  practical_exam_date: DateTime | null;
  theory_exam_result: "passed" | "failed" | "";
  practical_exam_result: "passed" | "failed" | "";
};

const theoryFields = [
  ["theoriestunden_Grundstoff", "Theoriestunden Grundstoff"],
  ["theoriestunden_Zusatzstoff", "Theoriestunden Zusatzstoff"],
] as const;
const practicalFields = [
  ["simulator_UE", "Simulator (UE)"],
  ["uebungsfahrt_UE", "Übungsfahrten gesamt (UE)"],
  ["autobahnfahrt_UE", "Autobahnfahrten (UE)"],
  ["nachtfahrt_UE", "Nachtfahrten (UE)"],
  ["ueberlandfahrt_UE", "Überlandfahrten (UE)"],
] as const;

const sectionSx = {
  bgcolor: "action.hover",
  borderRadius: 2,
  p: 2,
  "@media (max-width: 599.95px)": {
    "& .MuiInputBase-root": { minWidth: 0, width: "100%" },
  },
};
const sectionTitleSx = { fontWeight: 600 };

const validateUnits = (value: number | null | undefined) =>
  value == null || (Number.isInteger(value) && value >= 0 && value <= 2147483647)
    ? undefined
    : "Bitte gib eine nichtnegative ganze Zahl ein.";
const validateDate = (value: DateTime | null) => (!value || value.isValid ? true : "Bitte gib ein gültiges Datum ein.");

export function ExternalTrainingProgressDialog(
  props: {
    studentId: string;
    progress?: ExternalTrainingProgress;
    availableClasses: Array<{ id: DrivingLicenseClass; name: string }>;
  } & DialogProps,
) {
  const { studentId, progress, availableClasses, open, onClose } = props;
  const {
    dialogProps: confirmDialogProps,
    openDialog: openConfirmDialog,
    closeDialog: closeConfirmDialog,
  } = useDialog();

  const defaultValues: FormValues = {
    drivingLicenseClass: progress?.drivingLicenseClass ?? "",
    theoriestunden_Grundstoff: progress?.theoriestunden_Grundstoff ?? null,
    theoriestunden_Zusatzstoff: progress?.theoriestunden_Zusatzstoff ?? null,
    simulator_UE: progress?.simulator_UE ?? null,
    uebungsfahrt_UE: progress?.uebungsfahrt_UE ?? null,
    autobahnfahrt_UE: progress?.autobahnfahrt_UE ?? null,
    nachtfahrt_UE: progress?.nachtfahrt_UE ?? null,
    ueberlandfahrt_UE: progress?.ueberlandfahrt_UE ?? null,
    uebungsfahrt_mit_Schaltwagen_UE: progress?.uebungsfahrt_mit_Schaltwagen_UE ?? null,
    testfahrt_schaltkompetenz_date: progress?.testfahrt_schaltkompetenz_date
      ? DateTime.fromISO(progress.testfahrt_schaltkompetenz_date)
      : null,
    theory_exam_date: progress?.theory_exam_date ? DateTime.fromISO(progress.theory_exam_date) : null,
    theory_exam_result: progress?.theory_exam_result ?? "",
    practical_exam_date: progress?.practical_exam_date ? DateTime.fromISO(progress.practical_exam_date) : null,
    practical_exam_result: progress?.practical_exam_result ?? "",
  };

  const onSubmit = async (values: FormValues) => {
    const {
      drivingLicenseClass,
      theoriestunden_Grundstoff,
      theoriestunden_Zusatzstoff,
      simulator_UE,
      uebungsfahrt_UE,
      autobahnfahrt_UE,
      nachtfahrt_UE,
      ueberlandfahrt_UE,
      uebungsfahrt_mit_Schaltwagen_UE,
      testfahrt_schaltkompetenz_date,
      theory_exam_date,
      practical_exam_date,
      theory_exam_result,
      practical_exam_result,
    } = values;
    const selectedClass = progress?.drivingLicenseClass ?? DrivingLicenseClassEnum.parse(drivingLicenseClass);
    const body: ProgressBody = {
      theoriestunden_Grundstoff,
      theoriestunden_Zusatzstoff,
      simulator_UE,
      uebungsfahrt_UE,
      autobahnfahrt_UE,
      nachtfahrt_UE,
      ueberlandfahrt_UE,
      uebungsfahrt_mit_Schaltwagen_UE,
      testfahrt_schaltkompetenz_date: testfahrt_schaltkompetenz_date?.toISODate() ?? null,
      theory_exam_date: theory_exam_date?.toISODate() ?? null,
      theory_exam_result: theory_exam_result || null,
      practical_exam_date: practical_exam_date?.toISODate() ?? null,
      practical_exam_result: practical_exam_result || null,
    };
    await runSafelyWithServerClient(
      (client) =>
        progress
          ? client.updateExternalTrainingProgress(
              {
                ...body,
              },
              { params: { id: studentId, drivingLicenseClass: selectedClass } },
            )
          : client.createExternalTrainingProgress(body, {
              params: { id: studentId, drivingLicenseClass: selectedClass },
            }),
      {
        errorMessage: "Der externe Ausbildungsstand konnte nicht gespeichert werden.",
        successMessage: "Externer Ausbildungsstand gespeichert.",
        onSuccess: onClose,
      },
    );
  };

  const onDelete = async () => {
    if (!progress) return;
    await runSafelyWithServerClient(
      (client) =>
        client.deleteExternalTrainingProgress(undefined, {
          params: { id: studentId, drivingLicenseClass: progress.drivingLicenseClass },
        }),
      {
        errorMessage: "Der externe Ausbildungsstand konnte nicht gelöscht werden.",
        successMessage: "Externer Ausbildungsstand gelöscht.",
        onSuccess: () => {
          closeConfirmDialog();
          onClose();
        },
      },
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            "& > form": { display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" },
          },
        }}
      >
        <DialogTitle>
          {progress
            ? `Externen Ausbildungsstand Klasse ${progress.drivingLicenseClass} bearbeiten`
            : "Externen Ausbildungsstand anlegen"}
        </DialogTitle>
        <DialogCloseButton onClick={onClose} />
        <Form2<FormValues> onSubmit={onSubmit} defaultValues={defaultValues}>
          <DialogContent sx={{ minHeight: 0 }}>
            <Stack spacing={2}>
              <SelectInput
                source="drivingLicenseClass"
                label="Führerscheinklasse"
                options={
                  progress
                    ? [[progress.drivingLicenseClass, progress.drivingLicenseClass]]
                    : availableClasses.map(({ id, name }) => [id, name])
                }
                required
                disabled={!!progress}
                validate={(value) => (value ? true : "Bitte wähle eine Führerscheinklasse aus.")}
              />
              <Stack spacing={2}>
                <Stack spacing={1} sx={sectionSx}>
                  <Typography variant="body2" component="h3" sx={sectionTitleSx}>
                    Theorie
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    {theoryFields.map(([source, label]) => (
                      <NumberInput
                        key={source}
                        source={source}
                        label={label}
                        min={0}
                        max={2147483647}
                        step={1}
                        validate={validateUnits}
                        helperText={false}
                      />
                    ))}
                  </Stack>
                </Stack>
                <Stack spacing={1} sx={sectionSx}>
                  <Typography variant="body2" component="h3" sx={sectionTitleSx}>
                    Praxis
                  </Typography>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ rowGap: 1 }}
                  >
                    {practicalFields.map(([source, label]) => (
                      <NumberInput
                        key={source}
                        source={source}
                        label={label}
                        min={0}
                        max={2147483647}
                        step={1}
                        validate={validateUnits}
                        helperText={false}
                      />
                    ))}
                  </Stack>
                </Stack>
                <Stack spacing={1} sx={sectionSx}>
                  <Typography variant="body2" component="h3" sx={sectionTitleSx}>
                    B197
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <NumberInput
                      source="uebungsfahrt_mit_Schaltwagen_UE"
                      label="Übungsfahrten mit Schaltwagen (UE)"
                      min={0}
                      max={2147483647}
                      step={1}
                      validate={validateUnits}
                      helperText={false}
                    />
                    <DateInput
                      source="testfahrt_schaltkompetenz_date"
                      label="Testfahrt Schaltkompetenz"
                      required={false}
                      allowPastDates
                      showClearButton
                      validate={validateDate}
                    />
                  </Stack>
                </Stack>
                <Stack spacing={1} sx={sectionSx}>
                  <Typography variant="body2" component="h3" sx={sectionTitleSx}>
                    Prüfungen
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <DateInput
                      source="theory_exam_date"
                      label="Theorieprüfung"
                      required={false}
                      allowPastDates
                      showClearButton
                      validate={validateDate}
                    />
                    <SelectInput
                      source="theory_exam_result"
                      label="Ergebnis Theorieprüfung"
                      options={[
                        ["", "Keine Angabe"],
                        ["passed", "Bestanden"],
                        ["failed", "Nicht bestanden"],
                      ]}
                    />
                  </Stack>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <DateInput
                      source="practical_exam_date"
                      label="Praktische Prüfung"
                      required={false}
                      allowPastDates
                      showClearButton
                      validate={validateDate}
                    />
                    <SelectInput
                      source="practical_exam_result"
                      label="Ergebnis praktische Prüfung"
                      options={[
                        ["", "Keine Angabe"],
                        ["passed", "Bestanden"],
                        ["failed", "Nicht bestanden"],
                      ]}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "space-between" }}>
            {progress ? (
              <Button color="error" onClick={openConfirmDialog}>
                Löschen
              </Button>
            ) : (
              <span />
            )}
            <SaveButton2 />
          </DialogActions>
        </Form2>
      </Dialog>
      {progress && (
        <ConfirmDialog
          {...confirmDialogProps}
          title={`Externen Ausbildungsstand Klasse ${progress.drivingLicenseClass} löschen?`}
          message="Diese Aktion kann nicht rückgängig gemacht werden."
          confirmButtonLabel="Ja, Ausbildungsstand löschen"
          onConfirm={onDelete}
        />
      )}
    </>
  );
}
